import { useRef, useState, useCallback } from "react";

// Premium interactive reveal over a real avant/après photo.
// The photo is never altered — the user sweeps a luminous "cleaning" line
// across it (squeegee metaphor), with clear Avant / Après labels.
export default function BeforeAfter({ img, title, desc, testid }) {
    const ref = useRef(null);
    const [pos, setPos] = useState(58);
    const dragging = useRef(false);

    const setFromEvent = useCallback((clientX) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const p = ((clientX - r.left) / r.width) * 100;
        setPos(Math.max(4, Math.min(96, p)));
    }, []);

    const onPointerDown = (e) => {
        dragging.current = true;
        setFromEvent(e.clientX);
    };
    const onPointerMove = (e) => {
        if (e.buttons === 0) dragging.current = false;
        if (dragging.current) setFromEvent(e.clientX);
    };
    const onPointerUp = () => (dragging.current = false);
    const onHover = (e) => {
        if (!dragging.current) setFromEvent(e.clientX);
    };

    return (
        <div className="group" data-testid={testid}>
            <div
                ref={ref}
                className="relative overflow-hidden select-none touch-none border border-white/10 bg-ink"
                style={{ aspectRatio: "4 / 3", cursor: "ew-resize" }}
                onPointerDown={onPointerDown}
                onPointerMove={(e) => {
                    onPointerMove(e);
                    onHover(e);
                }}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerUp}
            >
                <img
                    src={img}
                    alt={title}
                    loading="lazy"
                    draggable="false"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                {/* luminous cleaning line */}
                <div
                    className="absolute top-0 bottom-0 pointer-events-none"
                    style={{
                        left: `calc(${pos}% - 60px)`,
                        width: "120px",
                        background:
                            "linear-gradient(90deg, transparent, rgba(46,123,255,0.12), rgba(180,210,255,0.55), rgba(46,123,255,0.12), transparent)",
                        filter: "blur(3px)",
                    }}
                />
                <div
                    className="absolute top-0 bottom-0 pointer-events-none"
                    style={{ left: `${pos}%`, width: "2px", background: "rgba(255,255,255,0.9)", boxShadow: "0 0 16px rgba(46,123,255,0.9)" }}
                />
                {/* handle */}
                <div
                    className="absolute z-10 flex items-center justify-center rounded-full bg-white text-navy shadow-lg pointer-events-none"
                    style={{ left: `${pos}%`, top: "50%", width: 44, height: 44, transform: "translate(-50%, -50%)" }}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="m15 18-6-6 6-6" /><path d="m9 6 6 6-6 6" transform="translate(-2 0)" />
                    </svg>
                </div>
                {/* labels */}
                <span className="absolute bottom-4 left-4 text-[11px] uppercase tracking-[0.2em] font-bold text-white bg-navy/70 backdrop-blur-sm px-3 py-1.5 font-body pointer-events-none">
                    Avant
                </span>
                <span className="absolute bottom-4 right-4 text-[11px] uppercase tracking-[0.2em] font-bold text-white bg-brand-red/80 backdrop-blur-sm px-3 py-1.5 font-body pointer-events-none">
                    Après
                </span>
            </div>
            <div className="pt-5">
                <h3 className="text-lg font-medium text-navy font-display">{title}</h3>
                <p className="text-sm text-slate-500 font-body mt-1">{desc}</p>
            </div>
        </div>
    );
}
